import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs/operators';

import {
  setRedeemDataList,
  setRedeemDateType,
  setRedeemEndDate,
  setRedeemPage,
  setRedeemStartDate,
  setRedeemStatus,
  setRedeemSummaryList,
  setRedeemType,
} from 'src/app/action/redeem.actions';
import { PageSize } from 'src/app/enumeration/pageSize';
import { ProviderType } from 'src/app/enumeration/provider-type';
import {
  RedeemStatusList,
  RedeemStatusListForRedeemAdmin,
  RedeemStatusListPB,
  RedeemStatusListWC,
  RedeemStatusListWCForRedeemAdmin,
} from 'src/app/enumeration/redeem-status';
import { UserLoginRole } from 'src/app/enumeration/roles';
import {
  selectDataList,
  selectEndDate,
  selectPage,
  selectRedeemDateType,
  selectRedeemStatus,
  selectRedeemType,
  selectStartDate,
  selectSummary,
} from 'src/app/selector/redeem.selectors';
import { RedeemApiService } from 'src/app/service/redeem/redeem-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { DownloadService } from 'src/app/utils/download/download.service';
import { isObjectEmpty } from 'src/app/utils/function-check';

declare var $: any;

@Component({
  selector: 'app-customer-redeem-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './customer-redeem-list.component.html',
  styleUrls: ['./customer-redeem-list.component.css'],
})
export class CustomerRedeemListComponent implements OnInit {
  // ─── Modern Functional Injection Engine ───
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(RedeemApiService);
  private readonly datePipe = inject(DatePipe);
  private readonly downloadService = inject(DownloadService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Fine-Grained Reactive Signals Architecture ───
  readonly loading = signal<boolean>(false);
  readonly apiDownloadLoading = signal<boolean>(false);
  readonly page = signal<number>(0);
  readonly pageSize = signal<number>(PageSize.REDEEM_LISTS);
  readonly allTotal = signal<number>(0);
  readonly results = signal<any>(null);

  // ─── Dynamic Row Tracker Arrays Signals ───
  readonly isRetryBtnLoading = signal<boolean[]>([]);
  readonly isBlockBtnLoading = signal<boolean[]>([]);
  readonly isSignBtnLoading = signal<boolean[]>([]);
  readonly isQueryBtnLoading = signal<boolean[]>([]);

  // ─── Component Configurations States ───
  redeemType: string = 'ALL';
  redeemStatus: string = 'ALL';
  phone: string | null = '';
  startDate: string = '';
  endDate: string = '';
  dateType: string = 'REDEEMED_TS';
  redeemableProvider: string = 'WAVEPAY';
  downloadFileName: string = '';
  infoRemark: string = '';
  redeemId: any;
  signAndFailedIndex: any;
  total: any;

  // ─── Security Authorization Privilege Flags ───
  isAccessRedeemAdmin: boolean = false;
  isRetryAccess: boolean = false;
  isSignItemAccess: boolean = false;
  isCustomerPage: boolean = false;
  isPagination: boolean = false;
  showRetryButton: boolean = false;
  showBlockButton: boolean = false;

  // ─── Boolean Visibility Framework Switches ───
  bolDownloadForRedeem: boolean = true;
  bolSummay: boolean = false;
  bolWKBZ: boolean = false;
  bolPBTU: boolean = false;

  // ─── Financial Accounting Balance Metrics ───
  totalRedeemedAmount: string = '0';
  totalPendingRedeemedAmount: string = '0';
  totalProcessedRedeemedAmount: string = '0';
  totalPaymentNameGroupWaveMon: string = '0';
  totalPaymentNameGroupKBZPay: string = '0';
  totalPaymentNameGroupAYA: string = '0';
  totalPaymentNameGroupPBTU: string = '0';

  // ─── Response Structure Local Context Cache Lines ───
  listDataResponse: any;
  summaryDataResponse: any;
  redeemStatusOption: any;

  // ─── Static Selection Configuration Mapping Options ───
  readonly redeemStatusOptionALL = RedeemStatusList;
  readonly redeemStatusOptionWCT = RedeemStatusListWC;
  readonly redeemStatusOptionPBT = RedeemStatusListPB;

  readonly redeemTypesOption = [
    { id: 'a', value: 'ALL', label: 'ALL' },
    { id: 'b', value: 'PHONE_BILL_TOPUP', label: 'PHONE_BILL_TOPUP' },
    { id: 'c', value: 'WALLET_CASH_TRANSFER', label: 'WALLET_CASH_TRANSFER' },
  ];

  ngOnInit(): void {
    this.hydrateAccessPrivileges();

    this.redeemStatusOption = this.redeemStatusOptionALL;
    const routeParams = this.route.snapshot.paramMap;
    this.phone = routeParams.get('phone');

    if (this.phone && this.phone !== '0') {
      this.isCustomerPage = true;
      this.listCustomers(this.phone);
    } else {
      this.getPreviousDataReloadWithAppState();
    }
  }

  /**
   * Initializes permission scopes dynamically from role control service arrays
   */
  private hydrateAccessPrivileges(): void {
    this.isAccessRedeemAdmin = this.checkAccessRoles.check([UserLoginRole.REDEEM_PAYMENT_ADMIN]);
    this.isRetryAccess = this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN]);
    this.isSignItemAccess = this.checkAccessRoles.check([UserLoginRole.REDEEM_PAYMENT_ADMIN]);

    if (this.isAccessRedeemAdmin) {
      this.setInitRedeemStatusData();
      this.bolDownloadForRedeem = false;
    }
  }

  /**
   * Restores transactional catalog history tracks cleanly using NgRx Store selector blocks
   */
  getPreviousDataReloadWithAppState(): void {
    this.store.pipe(select(selectRedeemType), takeUntilDestroyed(this.destroyRef))
      .subscribe((redeemType: any) => this.redeemType = redeemType);

    this.store.pipe(select(selectRedeemDateType), takeUntilDestroyed(this.destroyRef))
      .subscribe((redeemDateType: any) => this.dateType = redeemDateType);

    this.store.pipe(select(selectStartDate), takeUntilDestroyed(this.destroyRef))
      .subscribe((startDate: any) => this.startDate = startDate);

    this.store.pipe(select(selectEndDate), takeUntilDestroyed(this.destroyRef))
      .subscribe((endDate: any) => this.endDate = endDate);

    this.store.pipe(select(selectPage), takeUntilDestroyed(this.destroyRef))
      .subscribe((page: any) => this.page.set(page));

    this.onChange('');

    this.store.pipe(select(selectRedeemStatus), takeUntilDestroyed(this.destroyRef))
      .subscribe((redeemStatus: any) => this.redeemStatus = redeemStatus);

    this.store.pipe(select(selectDataList), takeUntilDestroyed(this.destroyRef))
      .subscribe((dataList: any) => {
        if (!isObjectEmpty(dataList)) {
          this.listDataResponse = dataList;
          this.setListDataResponse();
        }
      });

    this.store.pipe(select(selectSummary), takeUntilDestroyed(this.destroyRef))
      .subscribe((summaryList: any) => {
        if (!isObjectEmpty(summaryList)) {
          this.summaryDataResponse = summaryList;
          this.setSummaryDataListResponse();
        }
      });
  }


  setInitRedeemStatusData(): void {
    // Structural assignment matching administrator role privileges metrics
    (this as any).redeemStatusOptionALL = RedeemStatusListForRedeemAdmin;
    (this as any).redeemStatusOptionWCT = RedeemStatusListWCForRedeemAdmin;
  }

  /**
   * Ingests list matrices tied to a single unique customer record
   */
  listCustomers(phone: string): void {
    this.loading.set(true);
    this.listDataResponse = [];

    this.apiService
      .getRedeemedByCustomer(phone, this.redeemStatus, this.page())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v: any) => {
          this.listDataResponse = v;
          this.setListDataResponse();
        },
        error: (e: any) => {
          if (e) alert(e);
        }
      });
  }

  /**
   * Parses active server query data into fine-grained reactive view signals
   */
  setListDataResponse(): void {
    if (!this.listDataResponse?.Message) return;

    const msg = this.listDataResponse.Message;

    // Smoothly populate the new high-density template signal channels
    this.results.set(msg.redeemedItemInfoRespLst || []);
    this.total = msg.total;
    this.allTotal.set(msg.allTotal || 0);

    // Operational verification evaluation block for manual transaction retries
    const errorStatusesForRetry = [
      'AUTO_PROCESSED_ERROR_3',
      'AUTO_KBZPAY_ERROR_FAILED',
      'AUTO_KBZPAY_ERROR_UNKNOWN',
      'AUTO_KBZPAY_ERROR_PENDING',
      'WAVEPAY_ERROR_FAILED',
      'AYAPAY_ERROR_FAILED'
    ];
    this.showRetryButton = errorStatusesForRetry.includes(this.redeemStatus);

    // Operational evaluation validation check block for terminal block constraints
    const isKbzError = this.redeemStatus.includes('KBZPAY_ERROR');
    const isWaveError = this.redeemStatus.includes('WAVEPAY_ERROR');
    const isAyaError = this.redeemStatus.includes('AYAPAY_ERROR');

    this.showBlockButton = this.redeemStatus === 'AUTO_PROCESSED_ERROR_3' || isKbzError || isWaveError || isAyaError;
  }

  /**
   * Filter selection receiver matrix mapping parameters layout adjustments
   */
  onChange(e: any): void {
    if (this.isCustomerPage) return;

    switch (this.redeemType) {
      case 'PHONE_BILL_TOPUP':
        this.bolPBTU = true;
        this.bolSummay = true;
        this.bolWKBZ = false;
        if (this.isAccessRedeemAdmin) {
          this.bolDownloadForRedeem = false;
        }
        this.redeemStatusOption = this.redeemStatusOptionPBT;
        this.resetValue();
        break;

      case 'WALLET_CASH_TRANSFER':
        this.bolWKBZ = true;
        this.bolSummay = true;
        this.bolPBTU = false;
        if (this.isAccessRedeemAdmin) {
          this.bolDownloadForRedeem = true;
        }
        this.redeemStatusOption = this.redeemStatusOptionWCT;
        this.resetValue();
        break;

      case 'ALL':
      default:
        this.bolSummay = false;
        this.bolWKBZ = false;
        this.bolPBTU = false;
        if (this.isAccessRedeemAdmin) {
          this.bolDownloadForRedeem = false;
        }
        this.redeemStatusOption = this.redeemStatusOptionALL;
        this.resetValue();
        break;
    }
  }

  /**
   * Resets numerical financial aggregates and empties dataset vectors safely
   */
  resetValue(): void {
    this.totalRedeemedAmount = '0';
    this.totalPendingRedeemedAmount = '0';
    this.totalProcessedRedeemedAmount = '0';
    this.totalPaymentNameGroupWaveMon = '0';
    this.totalPaymentNameGroupKBZPay = '0';
    this.totalPaymentNameGroupPBTU = '0';
    this.totalPaymentNameGroupAYA = '0';
    this.redeemStatus = 'ALL';

    this.results.set([]); // Zero out array targets cleanly via signal modifiers
  }


  searchByCustomer(): void {
    this.page.set(0);
    this.getRedeemList(!this.isPagination);
  }

  /**
   * Orchestrates the primary paginated retrieval and optional conditional summary syncing
   */
  getRedeemList(isCallSummary: boolean): void {
    let start: string | null = null;
    let end: string | null = null;

    if (this.startDate.length < 1 && this.endDate.length > 0) {
      alert('Please select start date');
      return;
    } else if (this.startDate.length > 0 && this.endDate.length < 1) {
      alert('Please select end date');
      return;
    } else if (this.startDate.length > 0 && this.endDate.length > 0) {
      start = this.datePipe.transform(this.startDate, 'dd/MM/yyyy');
      end = this.datePipe.transform(this.endDate, 'dd/MM/yyyy');
    }

    this.loading.set(true);
    this.listDataResponse = [];

    this.apiService
      .getRedeemList(this.redeemType, this.redeemStatus, this.dateType, start, end, this.page())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (isCallSummary) {
            this.listItemsSummaryDateFilter(this.redeemType, start, end, this.page());
          } else {
            this.loading.set(false);
            this.saveNgrxStore();
          }
        })
      )
      .subscribe({
        next: (v: any) => {
          this.listDataResponse = v;
          this.setListDataResponse();
        },
        error: (e: any) => {
          if (e) alert(e);
        }
      });
  }

  /**
  * Retrieves summary analytical financial aggregates matching date filter boundaries
  */
  listItemsSummaryDateFilter(redeemType: string, start: string | null, end: string | null, page: number): void {
    if (this.redeemType === 'ALL') {
      this.loading.set(false);
      return;
    }

    this.summaryDataResponse = [];
    this.apiService
      .RedeemedItemByStatusServiceSummaryWithDateFilter(redeemType, this.dateType, start, end, page)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
          this.saveNgrxStore();
        })
      )
      .subscribe({
        next: (v: any) => {
          this.summaryDataResponse = v;
          this.setSummaryDataListResponse();
        },
        error: (e: any) => {
          if (e) alert(e);
        }
      });
  }


  /**
   * Maps summary numeric values into formatted localized currency strings natively
   */
  setSummaryDataListResponse(): void {
    if (!this.summaryDataResponse) return;

    const summary = this.summaryDataResponse;
    const groupAmt = summary.totalPaymentNameGroupAmount || {};

    // Standard fallback string assignment handles currency transforms on the UI template pipe layer cleanly
    this.totalRedeemedAmount = String(summary.totalRedeemedAmount || 0);
    this.totalProcessedRedeemedAmount = String(summary.totalProcessedRedeemedAmount || 0);
    this.totalPendingRedeemedAmount = String(summary.totalPendingRedeemedAmount || 0);

    this.totalPaymentNameGroupWaveMon = String(groupAmt['Wave Mon'] || 0);
    this.totalPaymentNameGroupKBZPay = String(groupAmt['KBZPay W'] || 0);
    this.totalPaymentNameGroupAYA = String(groupAmt['AYA Pay'] || 0);
    this.totalPaymentNameGroupPBTU = String(groupAmt['Phone Bill Top Up'] || 0);
  }

  /**
   * Authorization check rule logic for basic operator retry triggers
   */
  shouldRenderRetryButton(data: any): boolean {
    const errorStatuses = ['AUTO_PROCESSED_ERROR_3', 'WAVEPAY_ERROR_FAILED', 'AYAPAY_ERROR_FAILED'];
    const hasStatusMatch = errorStatuses.includes(this.redeemStatus) && errorStatuses.includes(data.redeemableStatus);
    const isExcludedId = [1096253, 1083218, 1071351, 1055132, 1055131].includes(data.id);

    return hasStatusMatch && !this.isCustomerPage && this.isAccessRedeemAdmin && !isExcludedId;
  }

  /**
   * Authorization check rule logic for higher-privileged admin retry triggers
   */
  shouldRenderAdminRetryButton(data: any): boolean {
    const isProcessedError3 = this.redeemStatus === 'AUTO_PROCESSED_ERROR_3' && data.redeemableStatus === 'AUTO_PROCESSED_ERROR_3';
    const isWaveError = this.redeemStatus === 'WAVEPAY_ERROR_FAILED' && data.redeemableStatus === 'WAVEPAY_ERROR_FAILED';
    const isAyaError = this.redeemStatus === 'AYAPAY_ERROR_FAILED' && data.redeemableStatus === 'AYAPAY_ERROR_FAILED';

    const kbzErrorList = ['AUTO_KBZPAY_ERROR_FAILED', 'AUTO_KBZPAY_ERROR_UNKNOWN', 'AUTO_KBZPAY_ERROR_BLOCK', 'AUTO_KBZPAY_ERROR_PENDING'];
    const isKBZPayError = kbzErrorList.includes(this.redeemStatus) && kbzErrorList.includes(data.redeemableStatus);
    const isExcludedId = [1096253, 1083218, 1071351, 1055132, 1055131].includes(data.id);

    return (isProcessedError3 || isKBZPayError || isWaveError || isAyaError) && !this.isCustomerPage && this.isRetryAccess && !isExcludedId;
  }

  /**
   * Authorization check rule logic for system boundary lock interventions
   */
  shouldRenderAdminBlockButton(data: any): boolean {
    const isProcessedError3 = this.redeemStatus === 'AUTO_PROCESSED_ERROR_3' && data.redeemableStatus === 'AUTO_PROCESSED_ERROR_3';
    const isKBZPayError = this.redeemStatus.includes('KBZPAY_ERROR') && data.redeemableStatus.includes('KBZPAY_ERROR');
    const isWAVEPayError = this.redeemStatus.includes('WAVEPAY_ERROR') && data.redeemableStatus.includes('WAVEPAY_ERROR');
    const isAYAPayError = this.redeemStatus.includes('AYAPAY_ERROR') && data.redeemableStatus.includes('AYAPAY_ERROR');
    const isExcludedId = [1096253, 1083218, 1071351, 1055132, 1055131].includes(data.id);

    return (isProcessedError3 || isKBZPayError || isWAVEPayError || isAYAPayError) && !this.isCustomerPage && this.isRetryAccess && !isExcludedId;
  }

  /**
   * Authorization check rule logic for active status validation hooks
   */
  shouldRenderQueryButton(data: any): boolean {
    const kbzQueryList = ['AUTO_KBZPAY_ERROR_FAILED', 'AUTO_KBZPAY_ERROR_UNKNOWN', 'AUTO_KBZPAY_ERROR_PENDING'];
    const isKBZPayError = kbzQueryList.includes(this.redeemStatus) && kbzQueryList.includes(data.redeemableStatus);

    return isKBZPayError && !this.isCustomerPage && this.isRetryAccess;
  }

  /**
   * Intercepts row operations parameters and maps to matching provider gateways
   */
  retryRedeem(value: any, index: number): void {
    const status = value.redeemableStatus || '';

    if (status === 'AUTO_PROCESSED_ERROR_3') {
      this.retryRedeemApi(value.id, ProviderType.Phone_Bill, index);
    } else if (status.includes('KBZPAY_ERROR')) {
      this.retryRedeemApi(value.id, ProviderType.KBZ, index);
    } else if (status.includes('WAVEPAY_ERROR')) {
      this.retryRedeemApi(value.id, ProviderType.WAVE, index);
    } else if (status.includes('AYAPAY_ERROR')) {
      this.retryRedeemApi(value.id, ProviderType.AYA, index);
    }
  }


  /**
 * Dispatches a network retry invocation request for a targeted transaction index
 */
  retryRedeemApi(id: string, type: string, index: number): void {
    if (!confirm('Retry redeem item')) return;

    this.toggleLoadingState(this.isRetryBtnLoading, index, true);

    this.apiService.getRetryRedeemService(id, type)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.toggleLoadingState(this.isRetryBtnLoading, index, false))
      )
      .subscribe({
        next: () => alert('Success'),
        error: (e: any) => { if (e) alert(e); },
        complete: () => this.getRedeemList(!this.isPagination)
      });
  }

  /**
   * Route gateway interceptor sorting specific provider types into unified blocking tracks
   */
  blockRedeem(value: { id: any; redeemableStatus: string }, index: number): void {
    const status = value.redeemableStatus || '';
    const idStr = String(value.id);

    if (status === 'AUTO_PROCESSED_ERROR_3') {
      this.executeBlockOperation(idStr, 'BORedeem', index);
    } else if (status.includes('KBZPAY_ERROR')) {
      this.executeBlockOperation(idStr, 'KBZRedeem', index);
    } else if (status.includes('WAVEPAY_ERROR')) {
      this.executeBlockOperation(idStr, 'WAVERedeem', index);
    } else if (status.includes('AYAPAY_ERROR')) {
      this.executeBlockOperation(idStr, 'AYARedeem', index);
    }
  }

  /**
   * Consolidated Single Structural Method replacing 4 identical legacy functions 
   */
  private executeBlockOperation(id: string, serviceType: 'BORedeem' | 'KBZRedeem' | 'WAVERedeem' | 'AYARedeem', index: number): void {
    const reason = prompt('Please Enter Reason');
    if (reason === null) return; // Auditor cancelled prompt transaction early

    if (!confirm('Are you sure!')) return;

    this.toggleLoadingState(this.isBlockBtnLoading, index, true);

    // Maps string parameters straight onto matching underlying service execution contexts natively
    const apiCall$ = this.apiService[`getBlock${serviceType}Service`](id, reason.trim());

    apiCall$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.toggleLoadingState(this.isBlockBtnLoading, index, false))
      )
      .subscribe({
        next: () => alert('Success'),
        error: (e: any) => { if (e) alert(e); },
        complete: () => this.getRedeemList(!this.isPagination)
      });
  }

  /**
   * Queries provider processing status logs for automated validation verification checks
   */
  queryBORedeem(id: string, index: number): void {
    if (!confirm('Query redeem item')) return;

    this.toggleLoadingState(this.isQueryBtnLoading, index, true);

    this.apiService.getQueryRedeemService(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.toggleLoadingState(this.isQueryBtnLoading, index, false))
      )
      .subscribe({
        next: () => alert('Success Query'),
        error: (e: any) => { if (e) alert(e); },
        complete: () => this.getRedeemList(!this.isPagination)
      });
  }

  /**
   * Intercepts and flags items inside backend reconciliation registers
   */
  signItem(id: string, index: number): void {
    if (!confirm('Sign this redeem item')) return;

    this.toggleLoadingState(this.isSignBtnLoading, index, true);

    this.apiService.getSignRedeemService(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.toggleLoadingState(this.isSignBtnLoading, index, false))
      )
      .subscribe({
        next: () => alert('Success'),
        error: (e: any) => { if (e) alert(e); },
        complete: () => this.getRedeemList(!this.isPagination)
      });
  }

  prepareFail(id: string, index: number): void {
    this.redeemId = id;
    this.signAndFailedIndex = index;
  }

  /**
   * Confirms manual transaction rejection notes within dynamic modal frames
   */
  confirmFail(): void {
    $('#showInfoFailedDialog').modal('hide');
    this.toggleLoadingState(this.isSignBtnLoading, this.signAndFailedIndex, true);

    this.apiService.getFailRedeemService(this.redeemId, this.redeemableProvider, this.infoRemark)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.toggleLoadingState(this.isSignBtnLoading, this.signAndFailedIndex, false))
      )
      .subscribe({
        next: () => alert('Success'),
        error: (e: any) => { if (e) alert(e); },
        complete: () => this.getRedeemList(!this.isPagination)
      });
  }

  /**
   * Resets active search filters back onto baseline values
   */
  resetForm(): void {
    this.redeemType = 'ALL';
    this.redeemStatusOption = this.redeemStatusOptionALL;
    this.startDate = '';
    this.endDate = '';
    this.bolSummay = false;
    this.bolWKBZ = false;
    this.bolPBTU = false;

    this.resetValue();
    this.listDataResponse = [];
    this.summaryDataResponse = [];
    this.saveNgrxStore();
  }

  /**
   * Clean, unified Signal state-array mutation modifier method helper
   */
  private toggleLoadingState(targetSignal: any, index: number, state: boolean): void {
    targetSignal.update((states: boolean[]) => {
      states[index] = state;
      return [...states];
    });
  }


  /**
 * Unified Single Extraction Method replacing 2 duplicate legacy file download functions
 */
  executeCSVDownload(operationType: 'direct' | 'request'): void {
    if (!this.downloadFileName?.trim()) {
      alert('Please enter a target file name');
      return;
    }

    let start: string | null = null;
    let end: string | null = null;

    if (this.startDate.length < 1 && this.endDate.length > 0) {
      alert('Please select start date');
      return;
    } else if (this.startDate.length > 0 && this.endDate.length < 1) {
      alert('Please select end date');
      return;
    } else if (this.startDate.length > 0 && this.endDate.length > 0) {
      start = this.datePipe.transform(this.startDate, 'dd/MM/yyyy');
      end = this.datePipe.transform(this.endDate, 'dd/MM/yyyy');
    }

    if (!this.downloadFileName.endsWith('.csv')) {
      this.downloadFileName += '.csv';
    }

    this.apiDownloadLoading.set(true);

    // Maps string triggers straight onto matching dynamic backend endpoints
    const apiCall$ = operationType === 'direct'
      ? this.apiService.getDownloadCSV(this.redeemType, this.redeemStatus, this.downloadFileName, this.dateType, start, end)
      : this.apiService.getInitDownloadCSV(this.redeemType, this.redeemStatus, this.downloadFileName, this.dateType, start, end);

    apiCall$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.apiDownloadLoading.set(false);
          this.downloadFileName = '';
          $('#downloadCSVModal').modal('hide');
        })
      )
      .subscribe({
        next: (data: any) => {
          this.downloadService.downloadCsvWithModifiedTime(data, this.downloadFileName);
        },
        error: (e: any) => { if (e) alert(e); }
      });
  }

  /**
   * Facade fallback links pointing to the consolidated download extraction engine
   */
  downloadFile(): void {
    this.executeCSVDownload('direct');
  }

  downloadRequestFile(): void {
    this.executeCSVDownload('request');
  }

  /**
   * Syncs user-driven layout page mutations securely over to the reactive tracking signal
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event - 1);
      this.getRedeemList(this.isPagination);
    }
  }

  /**
   * Evaluates string descriptors and navigates administrators to provider specific ledger panels
   */
  goToTransactionList(data: any): void {
    if (!data) return;

    let type = ProviderType.Phone_Bill;
    const itemName = data.redeemableItemName || '';

    if (itemName.startsWith('KBZPay')) {
      type = ProviderType.KBZ;
    } else if (itemName.startsWith('Wave Money')) {
      type = ProviderType.WAVE;
    } else if (itemName.startsWith('AYA Pay')) {
      type = ProviderType.AYA;
    }

    this.router.navigate(['nav/redeem-transaction-list/', data.id, type]);
  }

  goToDownloadListPage(): void {
    this.router.navigate(['nav/redeem-download-list']);
  }

  /**
   * Dispatches unified transaction filters down onto your global NgRx Store state tracking slices
   */
  saveNgrxStore(): void {
    this.store.dispatch(setRedeemType({ redeemType: this.redeemType }));
    this.store.dispatch(setRedeemDateType({ redeemDateType: this.dateType }));
    this.store.dispatch(setRedeemStatus({ redeemStatus: this.redeemStatus }));
    this.store.dispatch(setRedeemStartDate({ redeemStartDate: this.startDate }));
    this.store.dispatch(setRedeemEndDate({ redeemEndDate: this.endDate }));
    this.store.dispatch(setRedeemPage({ redeemPage: this.page() }));
    this.store.dispatch(setRedeemDataList({ redeemDataList: this.listDataResponse }));
    this.store.dispatch(setRedeemSummaryList({ redeemSummaryList: this.summaryDataResponse }));
  }

  /**
   * Browser-level clean native backward route jump tracking
   */
  goBack(): void {
    window.history.back();
  }

}
